// ============================================
// Account & Voice Config Types
// ============================================

export type AccountPlatform = 'twitter' | 'instagram' | 'threads' | 'bluesky' | 'truthsocial' | 'linkedin' | 'facebook'

export type VoiceMode = 'automated' | 'manual' | 'off'

export interface AccountVoiceConfig {
  mode: VoiceMode
  manual_description?: string
  voice_profile?: VoiceProfile | null
}

export interface ConnectedAccount {
  id: number
  platform: AccountPlatform
  platform_user_id: string
  username: string
  display_name?: string
  profile_image_url?: string
  connected_at: string
  voice_config: AccountVoiceConfig
}

export interface ManagedAccount extends ConnectedAccount {
  connection_status: 'connected' | 'reconnect_required'
  disconnected_at: string | null
  has_retained_voice_profile: boolean
  reconnect_supported: boolean
}

export interface AccountPermanentDeleteResponse {
  success: true
  message: string
}

// ============================================
// User & Auth Types
// ============================================

export interface UserPublic {
  id: number
  email: string
  created_at: string
  onboarding_step: number
  has_social_connected: boolean
  /** @deprecated Use has_social_connected instead */
  has_twitter_connected?: boolean
  accounts?: ConnectedAccount[]
}

export interface TwitterAccountPublic {
  twitter_user_id: string
  username: string
  display_name?: string
  profile_image_url?: string
  connected_at: string
}

export interface LoginRequest {
  email: string
  password: string
}

export interface RegisterRequest {
  email: string
  password: string
}

export interface LoginResponse {
  success: true
  user: UserPublic
  token: string
}

export interface RegisterResponse {
  success: true
  user: UserPublic
}

// ============================================
// Twitter OAuth Types
// ============================================

export interface TwitterOAuthStartResponse {
  success: true
  auth_url: string
}

export interface TwitterAccountResponse {
  success: true
  account: TwitterAccountPublic
}

// ============================================
// Voice Analysis Types
// ============================================

export interface VoiceAnalysisData {
  vocabulary_frequency: Record<string, number>
  avg_sentence_length: number
  emoji_usage: {
    frequency: number
    common_emojis: string[]
  }
  tone_score: number
  common_topics: string[]
  punctuation_style: {
    uses_ellipsis: boolean
    uses_dashes: boolean
    exclamation_frequency: number
  }
  sample_size: number
  analysis_summary: string
}

export interface VoiceProfile {
  id: number
  social_account_id: number
  analysis_data: VoiceAnalysisData
  created_at: string
  updated_at: string
}

export type VoiceAnalysisStatus = 'pending' | 'processing' | 'completed' | 'failed'

export interface VoiceAnalysisResponse {
  success: true
  status: VoiceAnalysisStatus
  message: string
  job_id?: number
}

export interface VoiceAnalysisStatusResponse {
  success: true
  status: VoiceAnalysisStatus
  message: string
  progress?: number
  voice_profile?: VoiceProfile
}

export interface VoiceProfileResponse {
  success: true
  voice_profile: VoiceProfile
}

// ============================================
// Content & Queue Types
// ============================================

export type ContentType = 'single_tweet' | 'thread' | 'quote_reply' | 'instagram_caption'
export type ToneOverride = 'casual' | 'professional' | 'humorous' | 'inspirational' | 'educational' | 'provocative'
export type ContextType = 'url' | 'text'

export type QueueStatus = 'draft' | 'approved' | 'rejected' | 'posted' | 'scheduled'

export interface GenerationContext {
  type: ContextType
  value: string
}

export interface GenerationParams {
  prompt: string
  content_type: ContentType
  tone_override: ToneOverride | null
  use_voice: boolean
  context: GenerationContext | null
  account_id?: number
}

export interface QueueItemAccount {
  username: string
  display_name?: string | null
  profile_image_url?: string | null
}

export interface ContentQueueItem {
  id: number
  social_account_id?: number | null
  account_id?: number | null
  content_text: string
  status: QueueStatus
  generation_prompt?: string | null
  /** @deprecated Use posted_platform_id */
  twitter_post_id?: string
  posted_platform_id?: string | null
  post_url?: string | null
  posted_at?: string | null
  created_at: string
  updated_at: string
  generation_params?: GenerationParams | null
  scheduled_for?: string | null
  /** @deprecated Use reply_to_platform_id */
  reply_to_tweet_id?: string | null
  reply_to_platform_id?: string | null
  account?: QueueItemAccount | null
  content_type?: ContentType
  platform?: AccountPlatform
  media_urls?: string[] | null
  media_type?: string | null
  thread_parts?: string[] | null
}

export interface ContentGenerateRequest {
  prompt: string
  count?: number
}

export interface ContentGenerateResponse {
  success: true
  job_id: number
  message: string
}

export type GenerationJobStatus = 'pending' | 'processing' | 'completed' | 'failed'

export interface GenerationJobStatusResponse {
  success: true
  status: GenerationJobStatus
  progress?: number
  message?: string
  error_message?: string
}

export interface RemixResponse {
  success: true
  job_id: number
  message: string
}

export interface QueueListResponse {
  success: true
  drafts: ContentQueueItem[]
  approved: ContentQueueItem[]
  scheduled: ContentQueueItem[]
  posted: ContentQueueItem[]
}

export interface QueueItemResponse {
  success: true
  item: ContentQueueItem
}

export interface QueueItemUpdateRequest {
  content_text?: string
  status?: QueueStatus
  media_urls?: string[]
}

export interface QueueItemUpdateResponse {
  success: true
  item: ContentQueueItem
}

/** @deprecated Use PostToPlatformResponse */
export interface PostToTwitterResponse {
  success: true
  item: ContentQueueItem
  tweet_url: string
}

export interface PostToPlatformResponse {
  success: true
  item: ContentQueueItem
  post_url: string
  post_id?: string
  message?: string
}

export interface MediaUploadResponse {
  success: true
  url: string
  key: string
}

export interface ScheduleContentResponse {
  success: true
  item: ContentQueueItem
}

export interface CancelScheduleResponse {
  success: true
  item: ContentQueueItem
}

// ============================================
// Analytics Types
// ============================================

export interface TweetMetrics {
  id: number
  content_queue_id: number
  impressions: number
  likes: number
  retweets: number
  replies: number
  quotes: number
  bookmarks: number
  engagement_rate: number
  fetched_at: string
}

export interface PostWithMetrics extends ContentQueueItem {
  metrics?: TweetMetrics
}

export interface AnalyticsListResponse {
  success: true
  posts: PostWithMetrics[]
  total_posts: number
  total_impressions: number
  total_likes: number
  total_retweets: number
  avg_engagement_rate: number
}

export interface BestPerformingPost {
  id: number
  content_text: string
  likes: number
  engagement_rate: number
}

export interface PostsByDay {
  date: string
  count: number
}

export interface AnalyticsSummary {
  period: string
  total_posts: number
  total_impressions: number
  total_likes: number
  total_retweets: number
  total_replies: number
  avg_engagement_rate: number
  best_performing_post: BestPerformingPost | null
  posts_by_day: PostsByDay[]
}

export interface AnalyticsSummaryResponse {
  success: true
  summary: AnalyticsSummary
}

export interface MetricsRefreshResponse {
  success: true
  metrics: TweetMetrics
}

// ============================================
// Settings Types
// ============================================

export interface UserSettings {
  user_id: number
  tone_preference: number
  email_notifications: boolean
  auto_post_enabled: boolean
  post_frequency: string | null
  preferred_post_times: string[] | null
  content_topics: string[] | null
  content_max_length: number | null
  email_on_post: boolean
  email_on_engagement: boolean
  email_digest: boolean
  created_at: string
  updated_at: string
}

export interface SettingsGetResponse {
  success: true
  settings: UserSettings
}

export interface SettingsUpdateRequest {
  tone_preference?: number
  email_notifications?: boolean
  auto_post_enabled?: boolean
  post_frequency?: string | null
  preferred_post_times?: string[] | null
  content_topics?: string[] | null
  content_max_length?: number | null
  email_on_post?: boolean
  email_on_engagement?: boolean
  email_digest?: boolean
}

export interface SettingsUpdateResponse {
  success: true
  settings: UserSettings
}

// ============================================
// Onboarding Types
// ============================================

export interface OnboardingNextStep {
  step: number
  title: string
  description: string
  action: string
}

export interface OnboardingProgress {
  user_id: number
  step_completed: number
  completed_at: string | null
}

export interface OnboardingProgressResponse {
  success: true
  progress: OnboardingProgress
  next_step: OnboardingNextStep | null
}

// ============================================
// Password Reset Types
// ============================================

export interface PasswordResetRequestResponse {
  success: true
  message: string
}

export interface PasswordResetVerifyResponse {
  success: true
  valid: boolean
  message: string
}

export interface PasswordResetCompleteResponse {
  success: true
  message: string
}

export interface PasswordChangeResponse {
  success: true
  message: string
}

// ============================================
// Billing & Subscription Types
// ============================================

export type PlanTier = 'free' | 'solo' | 'pro' | 'agency'

export interface CreateCheckoutSessionResponse {
  success: true
  checkout_url: string
  session_id: string
}

export interface SubscriptionStatus {
  plan: PlanTier
  status: 'active' | 'canceled' | 'past_due' | 'trialing'
  current_period_end: string
  cancel_at_period_end: boolean
}

export interface SubscriptionStatusResponse {
  success: true
  subscription: SubscriptionStatus
}

export interface UsageInfo {
  plan: PlanTier
  generations_used: number
  generations_limit: number
  generations_remaining: number
  is_unlimited: boolean
}

export interface UsageInfoResponse {
  success: true
  usage: UsageInfo
}

// ============================================
// API Error Types
// ============================================

export const ErrorCode = {
  // Auth errors
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  EMAIL_ALREADY_EXISTS: 'EMAIL_ALREADY_EXISTS',
  UNAUTHORIZED: 'UNAUTHORIZED',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',

  // Password reset errors
  PASSWORD_RESET_TOKEN_INVALID: 'PASSWORD_RESET_TOKEN_INVALID',
  PASSWORD_RESET_TOKEN_EXPIRED: 'PASSWORD_RESET_TOKEN_EXPIRED',
  PASSWORD_RESET_TOKEN_USED: 'PASSWORD_RESET_TOKEN_USED',
  CURRENT_PASSWORD_INCORRECT: 'CURRENT_PASSWORD_INCORRECT',

  // Twitter errors
  TWITTER_NOT_CONNECTED: 'TWITTER_NOT_CONNECTED',
  TWITTER_API_ERROR: 'TWITTER_API_ERROR',
  TWITTER_RATE_LIMIT: 'TWITTER_RATE_LIMIT',
  TWITTER_DISCONNECT_FAILED: 'TWITTER_DISCONNECT_FAILED',

  // Platform errors
  SOCIAL_ACCOUNT_NOT_CONNECTED: 'SOCIAL_ACCOUNT_NOT_CONNECTED',
  PLATFORM_API_ERROR: 'PLATFORM_API_ERROR',
  PLATFORM_RATE_LIMIT: 'PLATFORM_RATE_LIMIT',
  PLATFORM_AUTH_EXPIRED: 'PLATFORM_AUTH_EXPIRED',

  // Instagram errors
  INSTAGRAM_MEDIA_REQUIRED: 'INSTAGRAM_MEDIA_REQUIRED',
  INSTAGRAM_CONTAINER_FAILED: 'INSTAGRAM_CONTAINER_FAILED',

  // Media errors
  MEDIA_UPLOAD_FAILED: 'MEDIA_UPLOAD_FAILED',
  MEDIA_TOO_LARGE: 'MEDIA_TOO_LARGE',
  UNSUPPORTED_MEDIA_TYPE: 'UNSUPPORTED_MEDIA_TYPE',

  // Voice errors
  NO_VOICE_PROFILE: 'NO_VOICE_PROFILE',
  VOICE_ANALYSIS_IN_PROGRESS: 'VOICE_ANALYSIS_IN_PROGRESS',
  VOICE_ANALYSIS_FAILED: 'VOICE_ANALYSIS_FAILED',
  INSUFFICIENT_TWEETS: 'INSUFFICIENT_TWEETS',

  // Content errors
  CONTENT_NOT_FOUND: 'CONTENT_NOT_FOUND',
  INVALID_STATUS_TRANSITION: 'INVALID_STATUS_TRANSITION',
  CONTENT_ALREADY_POSTED: 'CONTENT_ALREADY_POSTED',

  // Schedule errors
  SCHEDULE_IN_PAST: 'SCHEDULE_IN_PAST',
  SCHEDULE_TOO_FAR: 'SCHEDULE_TOO_FAR',

  // LLM errors
  LLM_API_ERROR: 'LLM_API_ERROR',
  LLM_RATE_LIMIT: 'LLM_RATE_LIMIT',

  // Rate limit errors
  RATE_LIMIT: 'RATE_LIMIT',
  TOO_MANY_REQUESTS: 'TOO_MANY_REQUESTS',

  // Account errors
  ACCOUNT_NOT_FOUND: 'ACCOUNT_NOT_FOUND',
  ACCOUNT_MUST_BE_DISCONNECTED: 'ACCOUNT_MUST_BE_DISCONNECTED',

  // Billing errors
  GENERATION_LIMIT_REACHED: 'GENERATION_LIMIT_REACHED',

  // General errors
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  NOT_FOUND: 'NOT_FOUND',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  NETWORK_ERROR: 'NETWORK_ERROR',
} as const

export type ErrorCodeType = (typeof ErrorCode)[keyof typeof ErrorCode]

export interface ApiErrorResponse {
  success: false
  error: {
    code: ErrorCodeType
    message: string
    details?: Record<string, unknown>
  }
}
